import React, { useState } from 'react';
import { X } from 'lucide-react';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'COP', 'ARS', 'MXN', 'BRL'];
const STATUSES = ['active', 'paused', 'completed', 'cancelled'];

/**
 * GoalForm — modal form for creating or editing an investment goal.
 */
const GoalForm = ({ goal, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    name: goal?.name || '',
    description: goal?.description || '',
    target_amount_min: goal?.target_amount_min || '',
    target_amount_max: goal?.target_amount_max || '',
    currency: goal?.currency || 'USD',
    status: goal?.status || 'active',
    target_date: goal?.target_date
      ? new Date(goal.target_date).toISOString().split('T')[0]
      : '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.target_amount_min) errs.target_amount_min = 'Minimum target is required.';
    if (!form.target_amount_max) errs.target_amount_max = 'Maximum target is required.';
    if (
      form.target_amount_min &&
      form.target_amount_max &&
      parseFloat(form.target_amount_min) > parseFloat(form.target_amount_max)
    ) {
      errs.target_amount_max = 'Maximum must be greater than or equal to minimum.';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      await onSubmit({
        ...form,
        target_amount_min: parseFloat(form.target_amount_min),
        target_amount_max: parseFloat(form.target_amount_max),
        target_date: form.target_date || null,
      });
    } catch (err) {
      const apiErrors = err.response?.data || {};
      setErrors(apiErrors);
    } finally {
      setLoading(false);
    }
  };

  const field = (label, key, type = 'text', extras = {}) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setErrors({ ...errors, [key]: undefined }); }}
        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors[key] ? 'border-red-400' : 'border-gray-200'}`}
        {...extras}
      />
      {errors[key] && <p className="mt-1 text-xs text-red-500">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {goal ? 'Edit Goal' : 'New Investment Goal'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {field('Goal Name', 'name', 'text', { placeholder: 'e.g. Emergency Fund' })}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
              rows={3}
              placeholder="Brief description of your goal..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('Min Target ($)', 'target_amount_min', 'number', { min: 0, step: '0.01', placeholder: '1000' })}
            {field('Max Target ($)', 'target_amount_max', 'number', { min: 0, step: '0.01', placeholder: '5000' })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white capitalize"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {field('Target Date (optional)', 'target_date', 'date')}

          {errors.non_field_errors && (
            <p className="text-xs text-red-500">{errors.non_field_errors}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : goal ? 'Save Changes' : 'Create Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalForm;
